import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography, Container, Grid, IconButton, Link as MuiLink, useTheme, Paper } from '@mui/material';
import { styled } from '@mui/system';
import { motion, useScroll, useTransform, useAnimation } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import splashBackground from '../splashscreenbackground.jpg';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import desktopScreenshot1 from '../assets/desktop-screenshot-1.png';
import dealcostScreenshot from '../assets/dealcost_screenshot.png';
import dashboardScreenshot from '../assets/dashboard-screenshot.png';
import logo from '../assets/logo.png';
import inventoryManagementScreenshot from '../assets/inventory-management-feature.png';
import dealAnalysisOcrScreenshot from '../assets/deal-analysis-ocr-feature.png';
import financialManagementScreenshot from '../assets/financial-management-feature.png';
import taskManagementScreenshot from '../assets/task-management-feature.png';
import shortSkinnyImage from '../assets/short-skinny.png';
import portraitImage from '../assets/portrait.png';
import taskMobileList from '../assets/task-mobile-list.png';
import taskMobileDetail from '../assets/task-mobile-detail.png';

const Section = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  padding: 0,
  margin: 0
}));

const BackgroundImage = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  margin: 0,
  padding: 0,
}));

const Overlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  zIndex: 1,
});

const StatCard = styled(motion.div)({
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  backdropFilter: 'blur(10px)',
  padding: '1rem',
  borderRadius: '15px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: 'white',
  textAlign: 'center',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  transformStyle: 'preserve-3d',
  perspective: '1000px',
  minHeight: '150px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '& h3': {
    color: '#00f2fe',
    textShadow: '0 0 20px rgba(0, 242, 254, 0.3)'
  },
  '& h6': {
    color: 'rgba(255, 255, 255, 0.9)',
    textShadow: '0 0 10px rgba(255, 255, 255, 0.2)'
  }
});

const DetailBox = styled(motion.div)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '3rem 2rem',
  background: 'rgba(0, 0, 0, 0.9)',
  backdropFilter: 'blur(20px)',
  transformOrigin: 'bottom',
  maxHeight: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
});

const FeatureCard = styled(motion.div)({
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  padding: '2rem',
  borderRadius: '15px',
  color: 'white',
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
});

const FullScreenWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  margin: 0,
  padding: 0,
  minHeight: '100vh',
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#000',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8)), url(${splashBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed'
}));

const BrightBackground = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.8)), url(${splashBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  filter: 'brightness(3.00)'
});

const StatsSection = styled(Section)({
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',  // Matched to FeatureShowcaseSection overlay
    zIndex: 1
  }
});

const Footer = styled('footer')({
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  padding: '40px 0',
  color: 'white',
  position: 'relative',
  zIndex: 1,
});

const styles = `
  body {
    margin: 0 !important;
    padding: 0 !important;
    overflow-x: hidden !important;
  }
  
  #root {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

// Helper component for the stacked feature section layout
const FeatureShowcaseSection = ({ title, description, image, imageAlt, children }) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      py: { xs: 10, md: 16 }, 
      overflow: 'hidden',
      position: 'relative',  
      '&::before': {         
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        zIndex: 1
      }
    }}>
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'white',
              textAlign: 'center'
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.75)',
              lineHeight: 1.7,
              fontWeight: 400,
              mb: 6,
              maxWidth: '700px',
              mx: 'auto',
              textAlign: 'center'
            }}
          >
            {description}
          </Typography>
        </motion.div>
      </Container>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
        >
          <Paper
            elevation={12}
            sx={{
              overflow: 'hidden',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              maxWidth: '1000px',
              mx: 'auto'
            }}
          >
            <Box
              component="img"
              src={image}
              alt={imageAlt}
              sx={{
                width: '100%',
                display: 'block',
                objectFit: 'contain',
                borderRadius: '16px',
              }}
            />
          </Paper>
        </motion.div>
        {children}
      </Container>
    </Box>
  );
};

function SplashScreen() {
  const { scrollYProgress } = useScroll();
  const controls = useAnimation();
  const navigate = useNavigate();

  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  const scrollToNext = () => {
    const nextSection = document.querySelector('#stats-section');
    if (nextSection) {
      nextSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const stats = [
    {
      number: '100+',
      label: 'Active Dealerships',
      details: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'
    },
    {
      number: '$50M+',
      label: 'Monthly Transactions',
      details: 'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident.'
    },
    {
      number: '10000+',
      label: 'Vehicles Managed',
      details: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit.'
    },
    {
      number: '99.9%',
      label: 'Uptime',
      details: 'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus.'
    },
  ];

  const features = [
    {
      icon: <DirectionsCarIcon sx={{ fontSize: 50 }} />,
      title: 'Inventory Management',
      description: 'Track every vehicle from acquisition to sale with detailed history and documentation.',
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 50 }} />,
      title: 'Real-time Analytics',
      description: 'Make data-driven decisions with comprehensive reporting and insights.',
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 50 }} />,
      title: 'Expense Tracking',
      description: 'Monitor all costs and expenses with our advanced OCR technology.',
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 50 }} />,
      title: 'Performance Metrics',
      description: 'Track profitability and performance across your entire inventory.',
    },
  ];

  const featuresLinks = [
    { name: 'Inventory Management', path: '/features/inventory-management' },
    { name: 'Cost Tracking', path: '/features/cost-tracking' },
    { name: 'Performance Metrics', path: '/features/performance-metrics' },
    { name: 'Deal Analysis & OCR', path: '/features/deal-analysis' }
  ];

  const otherLinks = [
    { name: 'Pricing', path: '/pricing' },
    { name: 'Schedule Demo', path: '/demo' },
    { name: 'Get Started', path: '/create-account' },
    { name: 'Support', path: '/support' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  return (
    <>
      <FullScreenWrapper>
        <BrightBackground />
        <Overlay />
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          {/* Top navigation buttons */}
          <Box sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            display: 'flex',
            gap: 2,
            zIndex: 10
          }}>
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: 'white',
                borderRadius: '4px',
                textTransform: 'none',
                px: 3
              }}
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/create-account')}
              variant="contained"
              sx={{
                backgroundColor: '#1976d2',
                borderRadius: '4px',
                textTransform: 'none',
                px: 3
              }}
            >
              Create Account
            </Button>
          </Box>
          
          {/* Hero Section */}
          <Section>
            <motion.div
              style={{ opacity, scale, width: '100%' }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Container maxWidth="xl" sx={{ height: '100vh', display: 'flex', alignItems: 'center' }}>
                <Grid container alignItems="center" sx={{ height: '100%' }}>
                  {/* Left side - Text content */}
                  <Grid item xs={12} md={4} sx={{
                    pl: { xs: 0, md: 0 },
                    pr: { xs: 0, md: 0 },
                    ml: { xs: -4, md: -12 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative',
                    alignItems: 'flex-start'
                  }}>
                    <Typography
                      variant="h1"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                        letterSpacing: '4px',
                        mb: 4,
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                      }}
                    >
                      DEALCOST
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'white',
                        mt: 2,
                        opacity: 0.9,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                      }}
                    >
                      Revolutionizing{'\n'}
                      Dealership{'\n'}
                      Inventory{'\n'}
                      Cost and Maintenance Management
                    </Typography>
                    
                    <Box display="flex" mt={4} gap={2}>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/create-account')}
                        sx={{
                          backgroundColor: '#1976d2',
                          borderRadius: '8px',
                          height: '50px',
                          flex: 1,
                          textTransform: 'uppercase'
                        }}
                      >
                        GET STARTED
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{
                          color: 'white',
                          borderColor: 'white',
                          borderRadius: '8px',
                          height: '50px',
                          flex: 1,
                          textTransform: 'uppercase'
                        }}
                      >
                        LEARN MORE
                      </Button>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8} sx={{
                    position: 'relative',
                    height: '50vh',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    mt: -6
                  }}>
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      sx={{
                        position: 'absolute',
                        top: '0%',
                        right: '10%',
                        transform: 'translateY(-50%)',
                        width: '900px',
                      }}
                    >
                      <img
                        src={desktopScreenshot1}
                        alt="Inventory Table"
                        style={{
                          width: '140%',
                          display: 'block',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </motion.div>
            
            <IconButton
              onClick={scrollToNext}
              sx={{
                position: 'absolute',
                bottom: '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                zIndex: 3,
              }}
            >
              <motion.div
                animate={{ 
                  y: [0, 10, 0],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  ease: "easeInOut"
                }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 40 }} />
              </motion.div>
            </IconButton>
          </Section>

          {/* Stats Section */}
          <StatsSection sx={{
            minHeight: 'auto',
            position: 'relative',
            margin: 0,
            padding: '4rem 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <Container maxWidth="lg" sx={{ zIndex: 2, py: 0 }}>
                <Grid container spacing={3}>
                  {stats.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          type: "spring",
                          stiffness: 100,
                          damping: 20,
                          delay: index * 0.2
                        }}
                      >
                        <StatCard
                          whileHover="hover"
                          variants={{
                            hover: {
                              scale: 1.05,
                              rotateY: 10,
                              boxShadow: "0 25px 50px -12px rgba(0, 242, 254, 0.25)",
                              transition: {
                                duration: 0.3,
                                ease: "easeOut"
                              }
                            }
                          }}
                        >
                          <motion.div
                            initial={{ opacity: 1 }}
                            variants={{
                              hover: {
                                y: -20,
                                transition: {
                                  duration: 0.3,
                                  ease: "easeOut"
                                }
                              }
                            }}
                          >
                            <Typography 
                              variant="h3" 
                              sx={{ 
                                fontWeight: 'bold', 
                                mb: 1,
                                fontSize: '2.5rem',
                                color: '#00f2fe',
                                textShadow: '0 0 20px rgba(0, 242, 254, 0.5)',
                                background: 'linear-gradient(45deg, #00f2fe 30%, #4facfe 90%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                filter: 'brightness(1.2) contrast(1.1)'
                              }}
                            >
                              {stat.number}
                            </Typography>
                            <Typography 
                              variant="h6"
                              sx={{ 
                                fontSize: '1.1rem',
                                color: 'rgba(255, 255, 255, 0.95)',
                                letterSpacing: '0.5px',
                                textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </motion.div>

                          <DetailBox
                            variants={{
                              hover: {
                                y: 0,
                                opacity: 1,
                                transition: {
                                  duration: 0.3,
                                  ease: "easeOut"
                                }
                              }
                            }}
                            initial={{ y: "100%", opacity: 0 }}
                          >
                            <motion.div
                              variants={{
                                hover: {
                                  y: 0,
                                  opacity: 1,
                                  transition: {
                                    delay: 0.1,
                                    duration: 0.3
                                  }
                                }
                              }}
                              initial={{ y: 20, opacity: 0 }}
                            >
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  lineHeight: 1.6,
                                  color: '#4facfe',
                                  fontWeight: 500
                                }}
                              >
                                {stat.details}
                              </Typography>
                            </motion.div>
                          </DetailBox>
                        </StatCard>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </motion.div>
          </StatsSection>

          {/* Feature Showcase Sections (using the updated component) */}
          <FeatureShowcaseSection
            title="Comprehensive Inventory Control"
            description="Effortlessly track vehicles from acquisition to sale. Manage costs, documents, and status all in one place for complete oversight."
            image={inventoryManagementScreenshot}
            imageAlt="Inventory Management Dashboard"
          />

          <FeatureShowcaseSection
            title="Instant Deal Analysis & OCR"
            description="Scan invoices with OCR to automatically capture costs. Analyze deal profitability in seconds, including reconditioning and maintenance."
            image={dealAnalysisOcrScreenshot}
            imageAlt="Deal Analysis with OCR Scan"
          >
            <Box
              sx={{
                width: { xs: '100%', md: '95%' },
                maxWidth: '1000px',
                margin: '32px auto 0 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'rgba(16, 25, 38, 0.85)',
                borderRadius: '32px',
                p: { xs: 2, md: 4 }
              }}
            >
              <img
                src={shortSkinnyImage}
                alt="Short Skinny"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                  background: '#101926',
                  display: 'block'
                }}
              />
            </Box>
          </FeatureShowcaseSection>

          <FeatureShowcaseSection
            title="Streamlined Financial Tracking"
            description="Log every expense and deposit with ease. Categorize transactions, monitor cash flow, and gain clear insights into your dealership's financial health."
            image={financialManagementScreenshot}
            imageAlt="Financial Management Overview"
          />

          <FeatureShowcaseSection
            title="Organized Task Management"
            description="Assign tasks, set deadlines, and track progress. Keep your team aligned and ensure all operational duties are completed efficiently."
          >
            <Box
              sx={{
                width: { xs: '100%', md: '95%' },
                maxWidth: '700px',
                margin: '32px auto 0 auto',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: 4,
                background: 'rgba(16, 25, 38, 0.85)',
                borderRadius: '32px',
                p: { xs: 2, md: 4 }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', md: '50%' } }}>
                <img
                  src={taskMobileList}
                  alt="Mobile Task List"
                  style={{
                    width: '100%',
                    maxWidth: 250,
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                    background: '#101926',
                    display: 'block'
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.85)',
                    mt: 2,
                    textAlign: 'center'
                  }}
                >
                  View and search all assigned tasks, filter by status, and quickly access task history from your mobile device.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '100%', md: '50%' } }}>
                <img
                  src={taskMobileDetail}
                  alt="Mobile Task Detail"
                  style={{
                    width: '100%',
                    maxWidth: 250,
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                    background: '#101926',
                    display: 'block'
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.85)',
                    mt: 2,
                    textAlign: 'center'
                  }}
                >
                  Add new tasks, assign team members, set deadlines, and update statuses directly from your phone for seamless team coordination.
                </Typography>
              </Box>
            </Box>
          </FeatureShowcaseSection>

          {/* Product Showcase Section */}
          <Section sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            position: 'relative',
            margin: 0,
            padding: '6rem 0',
            overflow: 'hidden'
          }}>
            <Container maxWidth="xl">
              <Grid container spacing={4} alignItems="center">
                {/* Left side - Screenshot */}
                <Grid item xs={12} md={8}>
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <Box
                      component="img"
                      src={dealcostScreenshot}
                      alt="Product Screenshot"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transform: 'none',
                        '@media (max-width: 900px)': {
                          width: '100%',
                          transform: 'none',
                          mb: 6
                        }
                      }}
                    />
                  </motion.div>
                </Grid>

                {/* Right side - Text content */}
                <Grid item xs={12} md={4} sx={{  
                  pl: { md: 16 },
                  mt: { xs: 6, md: 0 },
                  position: 'relative',
                  zIndex: 1
                }}>
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        mb: 3,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        background: 'linear-gradient(45deg, #fff 30%, #00f2fe 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Streamline Your Operations
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        mb: 4,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        lineHeight: 1.8
                      }}
                    >
                      Gain complete control over individual vehicle costs. Track purchase price, maintenance expenses, and sale price to understand the true profit for every unit in your inventory. Log detailed maintenance records and visualize cost breakdowns effortlessly.
                    </Typography>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: '#00f2fe',
                          color: '#000',
                          padding: '12px 30px',
                          borderRadius: '50px',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#4facfe'
                          }
                        }}
                      >
                        Learn More
                      </Button>
                    </motion.div>
                  </motion.div>
                </Grid>
              </Grid>
            </Container>
          </Section>

          {/* Mirrored Product Showcase Section */}
          <Section sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            position: 'relative',
            margin: 0,
            padding: '6rem 0',
            overflow: 'hidden'
          }}>
            <Container maxWidth="xl">
              <Grid
                container
                spacing={0}
                alignItems="center"
                sx={{ position: 'relative' }}
              >
                {/* Left side - Text content */}
                <Grid item xs={12} md={4} sx={{
                  pr: { md: 16 },
                  mt: { xs: 6, md: 0 },
                  position: 'relative',
                  zIndex: 1,
                  order: { xs: 2, md: 1 }
                }}>
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        mb: 3,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        background: 'linear-gradient(45deg, #fff 30%, #00f2fe 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Monitor Your Performance
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        mb: 4,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        lineHeight: 1.8
                      }}
                    >
                      Get a high-level overview of your dealership's health with our intuitive dashboard. Visualize key metrics like total inventory value, average days on lot, profitability trends, and recent activity to make informed, data-driven decisions quickly.
                    </Typography>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: '#00f2fe',
                          color: '#000',
                          padding: '12px 30px',
                          borderRadius: '50px',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#4facfe'
                          }
                        }}
                      >
                        Learn More
                      </Button>
                    </motion.div>
                  </motion.div>
                </Grid>

                {/* Right side - Screenshot */}
                <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 2 } }}>
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    <Box
                      component="img"
                      src={dashboardScreenshot}
                      alt="Dashboard Screenshot"
                      sx={{
                        width: '90%',
                        height: 'auto',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(10%)',
                        display: 'block',
                        mx: 'auto',
                        filter: 'none',
                        '@media (max-width: 900px)': {
                          width: '100%',
                          transform: 'none',
                          mb: 6
                        }
                      }}
                    />
                  </motion.div>
                </Grid>
              </Grid>
            </Container>
          </Section>

        </Box>

      </FullScreenWrapper>
      <Footer>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Features Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                Features
              </Typography>
              {featuresLinks.map((item) => (
                <Typography
                  key={item.name}
                  variant="body2"
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#00f2fe', cursor: 'pointer' }
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Grid>

            {/* Links Column */}
            <Grid item xs={12} sm={3}>
               <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                 Company
               </Typography>
              {otherLinks.map((item) => (
                <Typography
                  key={item.name}
                  variant="body2"
                  component={item.path.startsWith('/') ? RouterLink : MuiLink}
                  to={item.path.startsWith('/') ? item.path : undefined}
                  href={!item.path.startsWith('/') ? item.path : undefined}
                  target={!item.path.startsWith('/') ? '_blank' : undefined}
                  rel={!item.path.startsWith('/') ? 'noopener noreferrer' : undefined}
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#00f2fe', cursor: 'pointer' }
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Grid>

            {/* Contact Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                CONTACT US TODAY
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: '#00f2fe',
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                202.820.1702
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#00f2fe',
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                dealcost24@gmail.com
              </Typography>
            </Grid>

            {/* Logo Column */}
            <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  letterSpacing: '2px'
                }}
              >
                DEALCOST
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                <MuiLink component={RouterLink} to="/terms" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#00f2fe' } }}>
                  Terms of Use
                </MuiLink>
                {' | '}
                <MuiLink component={RouterLink} to="/privacy" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#00f2fe' } }}>
                  Privacy Policy
                </MuiLink>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Copyright © {new Date().getFullYear()} DealCost LLC
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Footer>
    </>
  );
}

export default SplashScreen;